/**
 * Persists open windows for the lifetime of the browser tab, so a refresh
 * puts the desktop back exactly as it was. sessionStorage (not localStorage)
 * on purpose: a brand new visit should still boot into a clean desktop.
 */

const KEY = "win11-windows-v1";

const num = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);

export function loadSession(validAppIds) {
  if (typeof sessionStorage === "undefined") return null;
  let raw;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const data = JSON.parse(raw);
    if (!data || !Array.isArray(data.windows)) return null;

    const windows = data.windows
      .filter((w) => w && validAppIds.has(w.appId))
      .map((w) => ({
        id: String(w.id),
        appId: w.appId,
        x: num(w.x),
        y: num(w.y),
        w: num(w.w, 720),
        h: num(w.h, 520),
        z: num(w.z, 10),
        minimized: Boolean(w.minimized),
        maximized: Boolean(w.maximized),
        snap: w.snap || null,
        restoreRect: w.restoreRect || null,
        payload: w.payload || null,
        payloadKey: w.payloadKey || 0,
        // Skip the launch animation for windows that were already open.
        restored: true,
      }));

    const activeId = windows.some((w) => w.id === data.activeId)
      ? data.activeId
      : null;

    return {
      windows,
      activeId,
      zTop: windows.reduce((max, w) => Math.max(max, w.z), 10),
      opened: windows.length,
    };
  } catch {
    return null;
  }
}

export function saveSession(state) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ windows: state.windows, activeId: state.activeId })
    );
  } catch {
    /* private mode or quota — the desktop just won't be restored */
  }
}
